import {
  isChooseNoteStructureModalOpenAtom,
  postTranscriptionEntryModeAtom,
} from '@/atoms/noteStructureAtoms';
import { recordOfflineHandover } from '@/atoms/offline-recording/offline-handover';
import { pendingTranscribeModeBySessionIdAtom } from '@/atoms/offline-recording/pending-transcribe-mode';
import {
  GET_POST_TRANSCRIPTION_NOTE_ENTRY_VARIANT,
  IS_FIRST_NOTE_TEMPLATE_SELECTION_ENABLED,
  IS_POST_TRANSCRIPTION_NOTE_ENTRY_ASSIGNED,
} from '@/features/flags';
import { isFirstNoteUser } from '@/features/growth/first-note-templates/isFirstNoteUser';
import { isShadowHeidiEnabled } from '@/features/shadowHeidi/hooks/useIsShadowHeidiEnabled';
import { requestOfflineSyncQueueRefresh } from '@/hooks/desktop/offline-sync-queue-refresh';
import type { OfflineHandoverArgs } from '@/tauri/online-chunk-upload/session';
import {
  buildOfflineHandoverArgs,
  handoverNeedsTemplatePicker,
  sessionChoseAiCommand,
} from '@/utils/offline-recording/offlineHandover';
import { GET_SESSION_BY_ID, type QuerySessionResponse } from '@heidi/session/contracts';
import { getCurrentUser } from '@heidi/workspace/data';
import type { QueryClient } from '@tanstack/react-query';
import { getDefaultStore } from 'jotai';

/**
 * Where the online post-stop routing in `_actionPostRecording` shows a picker
 * instead of settling on a template, read the same way it reads them. Offline,
 * each of these opens the standard picker instead; the first-note selection's
 * own modal and the experiment's simplified layouts are online surfaces.
 */
function onlinePickerRouting(user: ReturnType<typeof getCurrentUser>) {
  const variant = IS_POST_TRANSCRIPTION_NOTE_ENTRY_ASSIGNED()
    ? GET_POST_TRANSCRIPTION_NOTE_ENTRY_VARIANT()
    : undefined;
  return {
    alwaysAsks: isShadowHeidiEnabled(),
    asksInsteadOfAuto:
      (IS_FIRST_NOTE_TEMPLATE_SELECTION_ENABLED() && isFirstNoteUser(user)) ||
      variant === 'simple_picker' ||
      variant === 'auto_first_picker',
    // Online, this arm generates an Auto note with no picker at all.
    generatesAuto: variant === 'auto_generate',
  };
}

/** Why the queue was re-read; shows in the refresh telemetry. */
export const OFFLINE_HANDOVER_REFRESH_REASON = 'offline_handover';

/**
 * The `offlineHandover` a stop sends, or `undefined` for a stop that must never
 * hand over. Read at stop time, not render, so an offline transcribe-mode
 * toggle made just before the stop is the one sent.
 *
 * Never sent:
 * - **on a pause.** It is a segment stop: the capture resumes online in the
 *   same session, so its unsent chunks stay with it.
 * - **with the offline-recording flag off.** Nothing else of offline recording
 *   is visible then, so no queued consult may appear either.
 * - **when the stop already owes the clinician a recovery prompt** (a silent
 *   capture, a WAV writer that failed, a capture reported truncated). Each
 *   has its own prompt, and a queued take would sync and generate a note
 *   behind it.
 * - **when the online stop would not generate a templated note**: a document
 *   or form template (`usesDocumentFlow`, which creates a document instead),
 *   or an AI command. The queued consult can only carry a template.
 * - **on an EHR-linked session with no specific template to use.** Auto must
 *   never be chosen silently there, and the sync cannot wait for an answer the
 *   clinician may never give; today's backup path keeps the note for them.
 */
export function offlineHandoverForStop({
  sessionId,
  isPaused,
  offlineRecordingEnabled,
  hasOwnRecoveryPrompt,
  usesDocumentFlow,
  queryClient,
}: {
  sessionId: string;
  isPaused: boolean;
  offlineRecordingEnabled: boolean;
  hasOwnRecoveryPrompt: boolean;
  usesDocumentFlow: boolean;
  queryClient: QueryClient;
}): OfflineHandoverArgs | undefined {
  if (isPaused || !offlineRecordingEnabled || hasOwnRecoveryPrompt || usesDocumentFlow) {
    return undefined;
  }
  const session = queryClient.getQueryData<QuerySessionResponse>([
    GET_SESSION_BY_ID,
    { sessionId },
  ]);
  // Without the session the stop cannot know its mode, its template or its
  // EHR link, and empty settings would let the sync default all three.
  if (!session || sessionChoseAiCommand(session)) {
    return undefined;
  }
  const user = getCurrentUser();
  const args = buildOfflineHandoverArgs({
    session,
    pickerRouting: onlinePickerRouting(user),
    pendingTranscribeMode: getDefaultStore().get(pendingTranscribeModeBySessionIdAtom)[
      sessionId
    ],
    defaultTemplateId: user?.session_settings?.default_template_id,
  });
  if (session.ehr_provider && handoverNeedsTemplatePicker(args)) {
    return undefined;
  }
  return args;
}

/**
 * What the app does once Rust reports it queued the stop's unsent audio on the
 * local consult `localSessionId`.
 *
 * 1. Remember the consult, so the picker can write to it before the queue is
 *    re-read.
 * 2. Re-read the queue: the handover emits no event of its own, and the
 *    session's "Saved offline" badge reads the queue.
 * 3. Open the template picker when no template was sent; otherwise the sync
 *    generates the note with the one that was.
 */
export function settleOfflineHandover({
  sessionId,
  localSessionId,
  args,
}: {
  sessionId: string;
  localSessionId: string;
  args: OfflineHandoverArgs | undefined;
}): void {
  recordOfflineHandover(sessionId, localSessionId);
  void requestOfflineSyncQueueRefresh(OFFLINE_HANDOVER_REFRESH_REASON);

  if (handoverNeedsTemplatePicker(args ?? {})) {
    const store = getDefaultStore();
    store.set(postTranscriptionEntryModeAtom, () => 'full');
    store.set(isChooseNoteStructureModalOpenAtom, () => true);
  }
}
